/** Phase 2A — Approvals queue (list + detail dialog with Approve / Reject / Withdraw). */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { auth } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const STATE_VARIANTS = {
  pending: "secondary", approved: "default", rejected: "destructive",
  withdrawn: "outline", expired: "outline",
};

const KINDS = ["campaign_publish", "campaign_budget_change", "asset_publish"];

function StateBadge({ state }) {
  return <Badge data-testid={`approval-state-${state}`} variant={STATE_VARIANTS[state] || "outline"}>{state}</Badge>;
}

export default function Approvals() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [state, setState] = useState(params.get("state") || "pending");
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const me = auth.getUser() || { role: "" };

  const load = async () => {
    setLoading(true);
    const p = {};
    if (state !== "all") p.state = state;
    if (kind !== "all") p.kind = kind;
    try { const r = await api.get("/approvals", { params: p }); setItems(r.data.items || []); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [state, kind]);

  // Open detail by URL ?id=<approval_id>
  useEffect(() => {
    const id = params.get("id");
    if (id && items.length) {
      const a = items.find((x) => x.id === id);
      if (a) setOpen(a);
    }
  }, [params, items]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const lc = q.toLowerCase();
    return items.filter((a) =>
      (a.kind || "").toLowerCase().includes(lc) ||
      (a.justification || "").toLowerCase().includes(lc) ||
      (a.resource_id || "").toLowerCase().includes(lc)
    );
  }, [items, q]);

  return (
    <div data-testid="approvals-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Approvals</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gated decisions — publish, budget changes, asset publishing.
          </p>
        </div>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              data-testid="approvals-search"
              placeholder="Search kind / resource / justification…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Select value={state} onValueChange={(v) => { setState(v); setParams({ state: v }); }}>
              <SelectTrigger data-testid="approvals-state-filter"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="withdrawn">Withdrawn</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="approvals-kind-filter"><SelectValue placeholder="Kind" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} data-testid="approvals-refresh">Refresh</Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kind</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Requested by</TableHead>
                <TableHead>Justification</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">Loading…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                  No approvals match the current filter.
                </TableCell></TableRow>
              )}
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`approval-row-${a.id}`} className="cursor-pointer hover:bg-accent/30" onClick={() => setOpen(a)}>
                  <TableCell className="font-mono text-xs">{a.kind}</TableCell>
                  <TableCell className="text-xs">{a.resource_type}/{(a.resource_id || "").slice(0, 8)}</TableCell>
                  <TableCell><StateBadge state={a.state} /></TableCell>
                  <TableCell className="text-xs">{(a.requested_by || "").slice(0, 8)}</TableCell>
                  <TableCell className="max-w-[260px] truncate text-xs">{a.justification || "—"}</TableCell>
                  <TableCell className="text-xs">{a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" data-testid={`approval-open-${a.id}`}>Open</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ApprovalDetail approval={open} onClose={() => { setOpen(null); setParams({}); }} onChanged={load} me={me} />
    </div>
  );
}

function ApprovalDetail({ approval, onClose, onChanged, me }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setComment(""); }, [approval?.id]);
  if (!approval) return null;
  const isOwn = approval.requested_by === me.id;
  const canApprove = ["owner", "admin", "approver"].includes(me.role);
  const canWithdraw = isOwn || ["owner", "admin"].includes(me.role);

  const act = async (suffix, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/approvals/${approval.id}/${suffix}`, body);
      toast.success(`${suffix} successful`);
      onChanged();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : JSON.stringify(d || "failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!approval} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="approval-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-sm">{approval.kind}</span>
            <StateBadge state={approval.state} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-muted-foreground">Ressource:</span> <span className="font-mono">{approval.resource_type}/{(approval.resource_id || "").slice(0, 12)}</span></div>
            <div><span className="text-muted-foreground">Angefragt am:</span> {approval.created_at ? new Date(approval.created_at).toLocaleString() : "—"}</div>
            {approval.decided_at && <div className="col-span-2"><span className="text-muted-foreground">Decided:</span> {new Date(approval.decided_at).toLocaleString()}</div>}
          </div>
          {approval.justification && (
            <div className="rounded-md border bg-muted/30 p-3 text-xs">
              <div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">Begründung</div>
              {approval.justification}
            </div>
          )}
          {approval.payload && Object.keys(approval.payload).length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Nutzdaten</summary>
              <pre className="mt-2 rounded bg-muted/30 p-2 overflow-auto text-[11px]">{JSON.stringify(approval.payload, null, 2)}</pre>
            </details>
          )}
          {approval.decision_comment && (
            <div className="rounded-md border p-3 text-xs">
              <div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">Decision comment</div>
              {approval.decision_comment}
            </div>
          )}
          {approval.state === "pending" && (
            <Textarea
              data-testid="approval-comment"
              placeholder="Kommentar (bei Ablehnung erforderlich)…"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </div>
        <DialogFooter className="gap-2">
          <DialogClose asChild><Button variant="ghost" data-testid="approval-close">Schließen</Button></DialogClose>
          {approval.state === "pending" && !canWithdraw && !canApprove && (
            <span className="text-xs text-muted-foreground" data-testid="approval-no-actions">
              You can view this approval but cannot act on it.
            </span>
          )}
          {approval.state === "pending" && canWithdraw && (
            <Button variant="outline" disabled={busy} onClick={() => act("withdraw")} data-testid="approval-withdraw">Zurückziehen</Button>
          )}
          {approval.state === "pending" && canApprove && (
            <>
              <Button variant="destructive" disabled={busy || !comment.trim()} onClick={() => act("reject", { comment })} data-testid="approval-reject">Ablehnen</Button>
              <Button disabled={busy} onClick={() => act("approve", { comment })} data-testid="approval-approve">Genehmigen</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
